import { useEffect, useState } from 'react';
import { ArrowLeft, Eye, LogIn } from 'lucide-react';
import { LEGACY_ROOM_CODE_ALPHABET, ROOM_CODE_MAX_LENGTH, isRoomCode, normalizeRoomCode } from '@gmp/shared-types/browser';
import { Stage } from '@gmp/shared-ui';
import { navigate } from '../app/router';
import { setPrefs, sound, usePrefs } from '../app/prefs';
import { party } from '../net/client';
import { SERVER_URL } from '../net/config';
import { usePartyState } from '../App';
import { ProfileFields } from './Profile';
import { SiteFooter } from '../app/Footer';

interface Summary { game: string; players: number; capacity: number; locked: boolean; approval: boolean; spectators: boolean; stage: string }

export function JoinParty({ initialCode }: { initialCode?: string }) {
  const prefs = usePrefs();
  const { status, error } = usePartyState();
  const [code, setCode] = useState(initialCode ?? '');
  const [nickname, setNickname] = useState(prefs.nickname);
  const [avatar, setAvatar] = useState(prefs.avatar);
  const [summary, setSummary] = useState<Summary | null | 'missing' | 'limited'>(null);
  const [busy, setBusy] = useState(false);
  // Older parties have 6-character codes with letters: offer a letter keyboard for those (and open with one
  // if the link already carries such a code).
  const [letters, setLetters] = useState(() => /[A-Z]/i.test(initialCode ?? ''));
  const clean = normalizeRoomCode(code);
  // 4 digits today (5 if the 4-digit space ever fills up); 6 characters for parties from before.
  const full = isRoomCode(clean);
  const hasLetters = /[A-Z]/.test(clean);

  useEffect(() => {
    setSummary(null);
    if (!full) return;
    const ctrl = new AbortController();
    fetch(`${SERVER_URL}/api/rooms/${clean}`, { signal: ctrl.signal })
      .then(async (r) => setSummary(r.ok ? ((await r.json()) as Summary) : r.status === 429 ? 'limited' : 'missing'))
      .catch(() => undefined);
    return () => ctrl.abort();
  }, [clean, full]);

  useEffect(() => {
    if (error) setBusy(false);
  }, [error]);

  function join(spectator = false) {
    setPrefs({ nickname: nickname.trim(), avatar });
    sound.play('submit');
    setBusy(true);
    party.joinRoom(clean, nickname.trim(), avatar, spectator);
  }

  const canJoin = full && nickname.trim().length > 0 && status === 'online' && summary !== 'missing' && summary !== 'limited' && !busy;
  return (
    <Stage>
      <main className="flex-1 flex flex-col gap-6 safe-pad safe-top safe-bottom py-6 max-w-lg w-full mx-auto">
        <header className="flex items-center gap-3">
          <button className="btn btn-sm btn-secondary" onClick={() => navigate({ name: 'home' })} aria-label="Back"><ArrowLeft size={18} /></button>
          <h1 className="font-display font-black text-3xl">Join a party</h1>
        </header>
        <label className="flex flex-col gap-2">
          <span className="text-sm font-bold text-mute">Party code</span>
          <input
            className="field font-display font-black text-5xl tracking-[0.3em] text-center uppercase tabular-nums"
            value={clean}
            onChange={(e) => setCode(e.target.value)}
            inputMode={letters ? 'text' : 'numeric'}
            pattern={letters ? '[A-Za-z0-9]*' : '[0-9]*'}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            maxLength={ROOM_CODE_MAX_LENGTH}
            placeholder="1234"
            aria-describedby="code-help"
            data-testid="code-input"
          />
          <span id="code-help" className="text-xs text-mute">
            The 4-digit number on the host’s screen.{' '}
            <button type="button" className="underline font-bold" onClick={() => setLetters(!letters)} data-testid="code-letters">
              {letters ? 'Use the number pad' : 'Older code with letters?'}
            </button>
          </span>
          {hasLetters && clean.split('').some((c) => !LEGACY_ROOM_CODE_ALPHABET.includes(c)) && <span className="text-bad text-sm">Party codes are 4 digits. Older codes with letters never use O, I, L, U, V, 0, 1 or 5. Double-check it.</span>}
        </label>
        {summary === 'missing' && <p className="chip bg-bad/20 border-bad py-2" role="alert">No party with that code right now.</p>}
        {summary === 'limited' && <p className="chip bg-bad/20 border-bad py-2" role="alert">Too many wrong codes from this network. Wait a few minutes, or scan the host’s QR code.</p>}
        {summary && summary !== 'missing' && summary !== 'limited' && (
          <p className="chip py-2 self-start">
            {summary.game} · {summary.players}/{summary.capacity} players{summary.locked ? ' · locked' : ''}{summary.approval ? ' · host approves joins' : ''}{summary.stage === 'IN_GAME' ? ' · game in progress (you’ll join the next one)' : ''}
          </p>
        )}
        <section className="card p-4"><ProfileFields nickname={nickname} avatar={avatar} onNickname={setNickname} onAvatar={setAvatar} /></section>
        <button className="btn btn-gold text-xl min-h-16" disabled={!canJoin} onClick={() => join(false)} data-testid="join-submit">
          <LogIn /> {status !== 'online' ? 'Connecting…' : busy ? 'Joining…' : 'Join'}
        </button>
        {summary && summary !== 'missing' && summary !== 'limited' && summary.spectators && (
          <button className="btn btn-ghost" disabled={!canJoin} onClick={() => join(true)}>
            <Eye size={18} /> Just watch
          </button>
        )}
        <SiteFooter className="mt-auto" />
      </main>
    </Stage>
  );
}
